import type { IPhoneNumber } from "../components/staff/PhoneNumber";

export type MemberSearchHiddenKeywordMatch = {
  fieldLabels: string[];
  excerpt: string;
};

export type MemberSearchProfessional = {
  id: string;
  name: string;
  lastName: string;
  photo?: string;
  generatedId?: number;
  legacyId?: string;
  title?: string;
  listTitle?: string;
  unlistedTitle?: string;
  listingCategory?: string;
  firmEmail?: string;
  phoneNumber?: string | IPhoneNumber;
  countryCodePhoneNumber?: string;
  firmName?: string;
  firmId?: string;
  firmLegacyId?: string;
  firmShortId?: string;
  firmServiceType?: string;
  firmCountry?: string;
  firmState?: string;
  subtitle: string;
  hiddenKeywordMatch?: MemberSearchHiddenKeywordMatch;
  matchPriority?: "primary" | "secondary";
  matchCount?: number;
};

export type MemberSearchFirm = {
  id: string;
  name: string;
  logo?: string;
  smallLogo?: string;
  legacyId?: string;
  shortId?: string;
  serviceType?: string;
  cityName?: string;
  stateName?: string;
  countryName?: string;
  subtitle: string;
  hiddenKeywordMatch?: MemberSearchHiddenKeywordMatch;
};

export type MemberSearchJurisdiction = {
  id: string;
  name: string;
  firmId: string;
  firmName: string;
  firmLegacyId?: string;
  firmShortId?: string;
};

export type MemberSearchSection<T> = {
  items: T[];
  hasMore: boolean;
  total: number;
};

export type MemberSearchEmptyReason =
  | "region-and-country"
  | "firm-outside-region"
  | "firm-outside-country"
  | "firm-outside-region-and-country";

export type MemberSearchResponse = {
  professionals: MemberSearchSection<MemberSearchProfessional>;
  firms: MemberSearchSection<MemberSearchFirm>;
  jurisdictions: MemberSearchSection<MemberSearchJurisdiction>;
  emptyReason?: MemberSearchEmptyReason;
};

const EMPTY_RESPONSE: MemberSearchResponse = {
  professionals: { items: [], hasMore: false, total: 0 },
  firms: { items: [], hasMore: false, total: 0 },
  jurisdictions: { items: [], hasMore: false, total: 0 },
};

const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const parts = document.cookie.split(";");
  for (let i = 0; i < parts.length; i += 1) {
    let part = parts[i];
    while (part.charAt(0) === " ") {
      part = part.substring(1);
    }
    if (part.indexOf(nameEQ) === 0) {
      return part.substring(nameEQ.length);
    }
  }
  return null;
};

const buildAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    "x-frontend-route": window.location.pathname,
  };

  let token = sessionStorage.getItem("token");
  let decoded = sessionStorage.getItem("token_decoded");
  if (!token) {
    token = getCookie("token");
  }
  if (!decoded) {
    decoded = getCookie("token_decoded");
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (decoded) {
    try {
      const role = JSON.parse(decoded)?.extension_WSG_Permissions as string | undefined;
      if (role) {
        headers["user-role"] = role;
      }
    } catch {
      // ignore malformed token payload
    }
  }

  return headers;
};

export type MemberSearchSectionKey = "professionals" | "firms" | "jurisdictions";

export type MemberSearchSectionPages = {
  professionalsPage?: number;
  firmsPage?: number;
  jurisdictionsPage?: number;
};

export const fetchMemberSearch = async (
  query: string,
  options?: {
    limit?: number;
    signal?: AbortSignal;
    lastNameOnly?: boolean;
    lastName?: string;
    firmId?: string;
    regionIds?: string[];
    countryIds?: string[];
    practiceIndustryId?: string;
    officeId?: string;
    officeIds?: string[];
    professionalTitleFilter?: string;
    serviceType?: string;
    /** Header quick search: match first/last name prefixes only. */
    quickSearch?: boolean;
    professionalsPage?: number;
    firmsPage?: number;
    jurisdictionsPage?: number;
    /** When set, only requested sections are queried on the server. */
    sections?: MemberSearchSectionKey[];
  },
): Promise<MemberSearchResponse> => {
  const trimmed = query.trim();
  if (
    !trimmed &&
    !options?.firmId &&
    !options?.regionIds?.length &&
    !options?.countryIds?.length &&
    !options?.practiceIndustryId &&
    !options?.officeId &&
    !options?.officeIds?.length &&
    !options?.professionalTitleFilter &&
    !options?.serviceType
  ) {
    return EMPTY_RESPONSE;
  }

  const params = new URLSearchParams();
  if (trimmed) {
    params.set("q", trimmed);
  }
  if (options?.limit) {
    params.set("limit", String(options.limit));
  }
  if (options?.lastNameOnly) {
    params.set("lastNameOnly", "true");
  }
  if (options?.lastName) {
    params.set("lastName", options.lastName);
  }
  if (options?.firmId) {
    params.set("firmId", options.firmId);
  }
  if (options?.regionIds?.length) {
    params.set("regionIds", options.regionIds.slice(0, 3).join(","));
  }
  if (options?.countryIds?.length) {
    params.set("countryIds", options.countryIds.slice(0, 3).join(","));
  }
  if (options?.practiceIndustryId) {
    params.set("practiceIndustryId", options.practiceIndustryId);
  }
  if (options?.officeIds?.length) {
    params.set("officeIds", options.officeIds.join(","));
  } else if (options?.officeId) {
    params.set("officeId", options.officeId);
  }
  if (options?.professionalTitleFilter) {
    params.set("professionalTitleFilter", options.professionalTitleFilter);
  }
  if (options?.serviceType) {
    params.set("serviceType", options.serviceType);
  }
  if (options?.quickSearch) {
    params.set("quickSearch", "true");
  }
  if (options?.professionalsPage && options.professionalsPage > 1) {
    params.set("professionalsPage", String(options.professionalsPage));
  }
  if (options?.firmsPage && options.firmsPage > 1) {
    params.set("firmsPage", String(options.firmsPage));
  }
  if (options?.jurisdictionsPage && options.jurisdictionsPage > 1) {
    params.set("jurisdictionsPage", String(options.jurisdictionsPage));
  }
  if (options?.sections?.length) {
    params.set("sections", options.sections.join(","));
  }

  const baseUrl = (process.env.REACT_APP_ROOT_API as string) || "";
  const response = await fetch(`${baseUrl}/memberSearch?${params.toString()}`, {
    method: "GET",
    headers: buildAuthHeaders(),
    signal: options?.signal,
  });

  if (!response.ok) {
    throw new Error(`Member search failed (${response.status})`);
  }

  return (await response.json()) as MemberSearchResponse;
};
